import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePostDetail } from "@hooks/useFetchDetail";
import { ServicesURL, uploadFile } from "@utils/utilsApi";
import { MethodType } from "src/services/type";
import CsvDiscardsModalCst, {
  CsvDiscardsFile,
} from "@components/CsvDiscardsModalCst";
import { openToast } from "@utils/toastStore";
import StaticGroupWizard from "../GroupWizard/StaticGroupWizard";
import {
  INITIAL_GROUP_WIZARD_DATA,
  StaticGroupWizardData,
} from "../GroupWizard/types";
import { enrollGroupToLearningObjects } from "../enrollGroupToLearningObjects";
import { updateUserStaticGroups } from "../updateUserStaticGroups";

const AddStaticGroup: React.FC = () => {
  const { id: corporateId = "" } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("group-wizard-v2");
  const backTo = `/v2/corporate/${corporateId}/detail/users-groups?tab=static-groups`;
  const [submitting, setSubmitting] = useState(false);
  const [csvDiscards, setCsvDiscards] = useState<CsvDiscardsFile | null>(null);

  const { trigger } = usePostDetail(
    `${ServicesURL.staticGroups}/static?id_corporate=${corporateId}`,
  );

  const onComplete = async (data: StaticGroupWizardData) => {
    setSubmitting(true);
    try {
      const res = await (trigger as any)({
        name: data.name || "",
        description: data.description || "",
      });
      let discards: CsvDiscardsFile | null = null;
      let csvUploaded = false;
      if (res?.id && data.pendingCsvFile) {
        try {
          const formData = new FormData();
          formData.append("file", data.pendingCsvFile);
          const uploadRes: any = await uploadFile(
            `${ServicesURL.staticGroups}/static/${res.id}`,
            formData,
            MethodType.PUT,
          );
          csvUploaded = true;
          const contentDisposition: string | undefined =
            uploadRes?.headers?.["content-disposition"];
          if (contentDisposition && contentDisposition.includes("filename")) {
            discards = {
              data: uploadRes.data,
              filename:
                contentDisposition
                  .split("filename=")[1]
                  ?.replace(/["']/g, "")
                  .trim() || "discards.csv",
              type: uploadRes?.headers?.["content-type"] || "text/csv",
            };
          }
        } catch (error) {
          console.error(error);
          openToast({
            severity: "error",
            message: String(t("csvUploadFailed")),
          });
        }
      }
      // TODO (BE Mancante): sostituire il ciclo di PUT per utente con
      // l'endpoint bulk (userIds sulla create del gruppo) quando disponibile.
      if (res?.id && data.userIds.length > 0) {
        const results = await Promise.allSettled(
          data.userIds.map((userId) =>
            updateUserStaticGroups(userId, {
              addGroupIds: [Number(res.id)],
              removeGroupIds: [],
            }),
          ),
        );
        const failed = results.filter((r) => r.status === "rejected").length;
        if (failed > 0) {
          openToast({
            severity: "error",
            message: String(
              t("usersAddPartial", {
                added: data.userIds.length - failed,
                total: data.userIds.length,
              }),
            ),
          });
        }
      }
      await enrollGroupToLearningObjects({
        corporateId,
        groupId: res?.id,
        groupType: "STATIC",
        loSelections: data.loSelections,
        enrollType: data.enrollConfig.enrollType || "TOP",
        config: data.enrollConfig,
      });
      if (discards) {
        setCsvDiscards(discards);
        setSubmitting(false);
        return;
      }
      if (csvUploaded) {
        openToast({
          severity: "success",
          message: String(t("csvUploadSuccess")),
        });
      }
      navigate(backTo);
    } catch (error) {
      setSubmitting(false);
      throw error;
    }
  };

  return (
    <>
      <CsvDiscardsModalCst
        open={!!csvDiscards}
        discards={csvDiscards}
        onClose={() => {
          setCsvDiscards(null);
          navigate(backTo);
        }}
      />
      <StaticGroupWizard
        initialData={INITIAL_GROUP_WIZARD_DATA}
        backTo={backTo}
        onComplete={onComplete}
        isCompleting={submitting}
      />
    </>
  );
};

export default AddStaticGroup;
